import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CartAddedToast } from '../components/CartAddedToast/CartAddedToast';
import {
  DIGIRASA_SUBSCRIPTION_CART_CLEARED_EVENT,
  SUBSCRIPTION_CART_STORAGE_KEY,
} from './subscriptionCartStorage';
import { mergeAddOnQuantities } from '../pages/OurMenu/cartAddOnUtils';
import {
  canChangeSubscriptionCartSlot,
  getSubscriptionCartLineTotal,
  normalizeSubscriptionCartItem,
  subscriptionCartLineKey,
} from '../utils/subscriptionCartItems';

const SubscriptionCartContext = createContext({
  subscriptionCartItems: [],
  addToSubscriptionCart: () => {},
  removeFromSubscriptionCart: () => {},
  updateSubscriptionCartQty: () => {},
  updateSubscriptionCartSlot: () => {},
  replaceSubscriptionCartLine: () => {},
  clearSubscriptionCart: () => {},
  subscriptionCartCount: 0,
  subscriptionCartTotal: 0,
});

const TOAST_DURATION_MS = 5500;

export const useSubscriptionCart = () => useContext(SubscriptionCartContext);

function readStoredItems() {
  try {
    const parsed = JSON.parse(localStorage.getItem(SUBSCRIPTION_CART_STORAGE_KEY));
    return Array.isArray(parsed) ? parsed.map((item) => normalizeSubscriptionCartItem(item)) : [];
  } catch {
    return [];
  }
}

export const SubscriptionCartProvider = ({ children }) => {
  const [items, setItems] = useState(readStoredItems);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(SUBSCRIPTION_CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    const onCleared = () => {
      setItems((prev) => (prev.length === 0 ? prev : []));
    };
    window.addEventListener(DIGIRASA_SUBSCRIPTION_CART_CLEARED_EVENT, onCleared);
    return () => window.removeEventListener(DIGIRASA_SUBSCRIPTION_CART_CLEARED_EVENT, onCleared);
  }, []);

  const dismissToast = useCallback(() => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToast(null);
  }, []);

  const showToast = useCallback(({ name, isRepeat, cartCount }) => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    setToast({
      name: name || 'Item',
      isRepeat,
      cartCount,
      key: Date.now(),
      kind: 'subscription',
    });
    toastTimerRef.current = window.setTimeout(() => {
      toastTimerRef.current = null;
      setToast(null);
    }, TOAST_DURATION_MS);
  }, []);

  useEffect(() => () => dismissToast(), [dismissToast]);

  const addToSubscriptionCart = useCallback((item, options = {}) => {
    const { quantity = 1, slotId, showToast: shouldToast = true, mode = 'add' } = options;
    const incoming = normalizeSubscriptionCartItem(item, { quantity, slotId });
    const lineKey = subscriptionCartLineKey(incoming);
    const existing = items.find((row) => subscriptionCartLineKey(row) === lineKey);
    const isRepeat = Boolean(existing);
    const nextCount = mode === 'upsert' && existing
      ? items.reduce((sum, row) => sum + row.quantity, 0)
      : items.reduce((sum, row) => sum + row.quantity, 0) + incoming.quantity;

    setItems((prev) => {
      const prevExisting = prev.find((row) => subscriptionCartLineKey(row) === lineKey);
      if (prevExisting && mode === 'upsert') {
        return prev.map((row) => (
          subscriptionCartLineKey(row) === lineKey
            ? { ...incoming, quantity: prevExisting.quantity }
            : row
        ));
      }
      if (prevExisting) {
        return prev.map((row) => (
          subscriptionCartLineKey(row) === lineKey
            ? normalizeSubscriptionCartItem({
                ...row,
                ...incoming,
                quantity: row.quantity + incoming.quantity,
                addOns: mergeAddOnQuantities(row.addOns, incoming.addOns),
              })
            : row
        ));
      }
      return [...prev, incoming];
    });

    const onCartPage =
      typeof window !== 'undefined'
      && (window.location.pathname === '/subscription-cart'
        || window.location.pathname === '/add-subscription');
    if (shouldToast && !onCartPage) {
      const addonHint =
        Array.isArray(incoming.addOns) && incoming.addOns.length > 0
          ? ` (+ ${incoming.addOns.map((a) => a.name).join(', ')})`
          : '';
      showToast({
        name: `${incoming.name}${addonHint}`,
        isRepeat,
        cartCount: nextCount,
      });
    }
  }, [items, showToast]);

  const removeFromSubscriptionCart = useCallback((id, slotId, options = {}) => {
    const lineKey = options.lineKey != null ? String(options.lineKey) : null;
    setItems((prev) => prev.filter((row) => {
      if (lineKey) return subscriptionCartLineKey(row) !== lineKey;
      if (String(row.fullDayGroupId || '') === String(id) || String(row.id) === String(id)) {
        if (!slotId || row.kind === 'full_day') return false;
        return row.slotId !== slotId;
      }
      return true;
    }));
  }, []);

  const updateSubscriptionCartQty = useCallback((id, slotId, qty, options = {}) => {
    const lineKey = options.lineKey != null ? String(options.lineKey) : null;
    if (qty < 1) {
      removeFromSubscriptionCart(id, slotId, { lineKey });
      return;
    }
    setItems((prev) => prev.map((row) => {
      if (lineKey) {
        return subscriptionCartLineKey(row) === lineKey
          ? { ...row, quantity: qty }
          : row;
      }
      const sameId = String(row.id) === String(id) || String(row.fullDayGroupId || '') === String(id);
      if (!sameId) return row;
      if (slotId && row.kind !== 'full_day' && row.slotId !== slotId) return row;
      return { ...row, quantity: qty };
    }));
  }, [removeFromSubscriptionCart]);

  const updateSubscriptionCartSlot = useCallback((id, fromSlotId, toSlotId, options = {}) => {
    setItems((prev) => {
      if (!toSlotId || fromSlotId === toSlotId) return prev;
      const fromKey = options.lineKey != null ? String(options.lineKey) : null;
      const line = fromKey
        ? prev.find((row) => subscriptionCartLineKey(row) === fromKey)
        : prev.find((row) => String(row.id) === String(id) && row.slotId === fromSlotId);
      if (!line || !canChangeSubscriptionCartSlot(line)) return prev;
      const without = fromKey
        ? prev.filter((row) => subscriptionCartLineKey(row) !== fromKey)
        : prev.filter((row) => !(String(row.id) === String(id) && row.slotId === fromSlotId));
      const moved = normalizeSubscriptionCartItem({ ...line, slotId: toSlotId });
      const existing = without.find((row) => subscriptionCartLineKey(row) === subscriptionCartLineKey(moved));
      if (existing) {
        return without.map((row) => (
          subscriptionCartLineKey(row) === subscriptionCartLineKey(moved)
            ? normalizeSubscriptionCartItem({
                ...row,
                quantity: row.quantity + line.quantity,
                addOns: mergeAddOnQuantities(row.addOns, line.addOns),
              })
            : row
        ));
      }
      return [...without, moved];
    });
  }, []);

  const replaceSubscriptionCartLine = useCallback((oldLineKey, nextItem, options = {}) => {
    const fromKey = oldLineKey != null ? String(oldLineKey) : null;
    if (!fromKey) return;
    const qtyOverride =
      options.quantity != null
        ? Math.max(1, Math.round(Number(options.quantity) || 1))
        : null;
    const slotOverride = options.slotId || null;

    setItems((prev) => {
      const oldLine = prev.find((row) => subscriptionCartLineKey(row) === fromKey);
      if (!oldLine) return prev;

      const incoming = normalizeSubscriptionCartItem(nextItem, {
        quantity: qtyOverride != null ? qtyOverride : oldLine.quantity,
        slotId: slotOverride || nextItem?.slotId || oldLine.slotId,
      });
      const newKey = subscriptionCartLineKey(incoming);
      const withoutOld = prev.filter((row) => subscriptionCartLineKey(row) !== fromKey);
      const existingNew = withoutOld.find((row) => subscriptionCartLineKey(row) === newKey);

      if (existingNew) {
        return withoutOld.map((row) => (
          subscriptionCartLineKey(row) === newKey
            ? normalizeSubscriptionCartItem({
                ...row,
                ...incoming,
                quantity: row.quantity + incoming.quantity,
                addOns: mergeAddOnQuantities(row.addOns, incoming.addOns),
              })
            : row
        ));
      }
      return [...withoutOld, incoming];
    });
  }, []);

  const clearSubscriptionCart = useCallback(() => {
    setItems((prev) => (prev.length === 0 ? prev : []));
  }, []);

  const subscriptionCartCount = items.reduce((sum, row) => sum + row.quantity, 0);
  const subscriptionCartTotal = items.reduce(
    (sum, row) => sum + getSubscriptionCartLineTotal(row),
    0,
  );

  return (
    <SubscriptionCartContext.Provider
      value={{
        subscriptionCartItems: items,
        addToSubscriptionCart,
        removeFromSubscriptionCart,
        updateSubscriptionCartQty,
        updateSubscriptionCartSlot,
        replaceSubscriptionCartLine,
        clearSubscriptionCart,
        subscriptionCartCount,
        subscriptionCartTotal,
      }}
    >
      {children}
      <CartAddedToast toast={toast} onDismiss={dismissToast} />
    </SubscriptionCartContext.Provider>
  );
};
